import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit as fsLimit,
  orderBy,
  query,
  startAfter,
  where,
  type DocumentData,
  type QueryConstraint,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { firestoreDb, firebaseFunctions } from './firebase'
import { callable } from './callable'
import type {
  SupportTicket,
  SupportTicketPriority,
  SupportTicketStatus,
  SupportReporterRestriction,
  SupportMacro,
  SupportMacroStep,
} from '../types/supportTicket'
export type { SupportTag, SupportMacro, SupportMacroStep } from '../types/supportTicket'

export type SupportSavedViewId =
  | 'open'
  | 'awaiting-me'
  | 'snoozed'
  | 'breached-sla'
  | 'bundled'
  | 'recently-resolved'
  | 'all'

export interface SupportTicketFilters {
  workspaceId?: string
  savedView?: SupportSavedViewId
  status?: SupportTicketStatus[]
  priority?: SupportTicketPriority[]
  tags?: string[]
  hasBundle?: boolean
  hasAttachments?: boolean
  hasRestriction?: boolean
  q?: string
  limit?: number
}

export interface SupportTicketCursor {
  id: string
  _docSnap?: QueryDocumentSnapshot<DocumentData>
}

export interface ListSupportTicketsResult {
  tickets: SupportTicket[]
  nextCursor: SupportTicketCursor | null
  hasMore: boolean
}

export interface SavedViewStats {
  count: number
  hasBreach: boolean
}

export type SupportSavedViewCounts = Record<SupportSavedViewId, SavedViewStats>

export interface UpdateSupportTicketParams {
  ticketId: string
  status?: SupportTicketStatus
  priority?: SupportTicketPriority
  tags?: string[]
  snoozedUntil?: string | null
  assignee?: string | null
  ownerNote?: string
  resolutionNote?: string
}

export interface UpdateReporterRestrictionParams {
  reporterUid: string
  blockNewTickets?: boolean
  blockReopens?: boolean
  slowMode?: number
  autoTag?: string
  skipAutoP0?: boolean
  broadcastOptOut?: boolean
  reason?: string
  expiresAt?: string | null
}

/**
 * Builds query constraints for support tickets based on filters and cursor.
 */
export function buildSupportQueryConstraints(
  filters: SupportTicketFilters = {},
  cursor?: SupportTicketCursor | QueryDocumentSnapshot<DocumentData> | null,
): QueryConstraint[] {
  const constraints: QueryConstraint[] = []
  const limitCount = filters.limit ?? 50
  if (filters.workspaceId?.trim()) constraints.push(where('workspaceId', '==', filters.workspaceId.trim()))

  // 1. Saved view specific constraints
  if (filters.savedView === 'awaiting-me') {
    constraints.push(where('status', '==', 'waiting-on-owner'))
  } else if (filters.savedView === 'snoozed') {
    constraints.push(where('status', '==', 'snoozed'))
  } else if (filters.savedView === 'breached-sla') {
    constraints.push(where('slaBreached', '==', true))
  }

  // 2. Explicit status filter (overrides saved view if specified)
  if (filters.status && filters.status.length === 1) {
    constraints.push(where('status', '==', filters.status[0]))
  }

  // Default ordering by updatedAt DESC
  constraints.push(orderBy('updatedAt', 'desc'))
  constraints.push(fsLimit(limitCount))

  if (cursor) {
    if ('_docSnap' in cursor && cursor._docSnap) {
      constraints.push(startAfter(cursor._docSnap))
    } else if ('data' in cursor && typeof cursor.data === 'function') {
      constraints.push(startAfter(cursor))
    }
  }

  return constraints
}

/**
 * Lists support tickets with in-memory filtering for multi-attribute fields and client-side search.
 * Routed via server callable with support_access_log recording.
 */
export async function listSupportTickets(
  filters: SupportTicketFilters = {},
  cursor?: SupportTicketCursor | null,
  accessReason?: { code: string; linkedId?: string },
): Promise<ListSupportTicketsResult> {
  return callable<{ filters: SupportTicketFilters; cursor?: { id: string } | null; accessReason?: unknown }, ListSupportTicketsResult>(
    'listOwnerSupportTickets',
    {
      filters,
      cursor: cursor ? { id: cursor.id } : null,
      accessReason,
    },
  )
}

/**
 * Retrieves a single support ticket by ID via server callable.
 */
export async function getSupportTicketById(
  id: string,
  accessReason?: { code?: string; ticketId?: string; linkedId?: string } | unknown,
): Promise<SupportTicket | null> {
  if (!id || !id.trim()) return null
  const parsedReason = accessReason ?? { code: 'SUPPORT_TICKET', ticketId: id.trim(), linkedId: id.trim() }
  return callable<{ ticketId: string; accessReason?: unknown }, SupportTicket | null>(
    'getOwnerSupportTicket',
    {
      ticketId: id.trim(),
      accessReason: parsedReason,
    },
  )
}

/**
 * Computes counts for the 7 prebuilt saved views via server callable.
 */
export async function getSupportSavedViewCounts(): Promise<SupportSavedViewCounts> {
  return callable<Record<string, never>, SupportSavedViewCounts>(
    'getOwnerSupportSavedViewCounts',
    {},
  )
}

/**
 * Mutates support ticket via server callable and triggers audit logging.
 */
export async function updateSupportTicket(params: UpdateSupportTicketParams): Promise<{ ok: boolean }> {
  const fn = httpsCallable<UpdateSupportTicketParams, { ok: boolean }>(firebaseFunctions, 'updateSupportTicket')
  const res = await fn(params)
  return res.data
}

/**
 * Retrieves reporter restriction from support_reporter_restrictions.
 */
export async function getSupportReporterRestriction(reporterUid: string): Promise<SupportReporterRestriction | null> {
  if (!reporterUid || !reporterUid.trim()) return null
  const snap = await getDoc(doc(firestoreDb, 'support_reporter_restrictions', reporterUid.trim()))
  if (!snap.exists()) return null
  return {
    ...(snap.data() as SupportReporterRestriction),
    reporterUid: snap.id,
  }
}

/**
 * Updates reporter restriction via server callable and writes audit log.
 */
export async function updateSupportReporterRestriction(
  params: UpdateReporterRestrictionParams,
): Promise<{ ok: boolean }> {
  const fn = httpsCallable<UpdateReporterRestrictionParams, { ok: boolean }>(
    firebaseFunctions,
    'updateSupportReporterRestriction',
  )
  const res = await fn(params)
  return res.data
}

export interface AddSupportTicketReplyParams {
  ticketId: string
  body: string
  mode: 'reply' | 'internal_note'
  nextStatus?: SupportTicketStatus
  resolutionReason?: string
  channels?: Array<'IN_APP' | 'PUSH' | 'EMAIL'>
}

export interface AddSupportTicketReplyResult {
  ok: boolean
  ticketId: string
  replyId?: string
  noteId?: string
  mode: 'reply' | 'internal_note'
  deliveryResults?: Record<string, { status: string; reason?: string; error?: string; attemptedAt?: string }>
  status?: SupportTicketStatus
  slaBreached?: boolean
}

/**
 * Sends a reply or saves an internal note on a support ticket via server callable.
 */
export async function addSupportTicketReply(
  params: AddSupportTicketReplyParams,
): Promise<AddSupportTicketReplyResult> {
  const fn = httpsCallable<AddSupportTicketReplyParams, AddSupportTicketReplyResult>(
    firebaseFunctions,
    'addSupportTicketReply',
  )
  const res = await fn(params)
  return res.data
}

export interface SupportTicketInternalNotesResult {
  ok: boolean
  ticketId: string
  notes: Array<{
    id: string
    ticketId: string
    authorId: string
    authorName: string
    authorRole?: string
    body: string
    createdAt: string
  }>
  summary: {
    ownerNote: string
    resolutionNote: string
  }
  /** Internal delivery metadata keyed by replyId — never on reporter-readable doc */
  replyMeta: Record<string, {
    authorId: string
    authorRole: string
    deliveryResults: Record<string, { status: string; reason?: string; error?: string; attemptedAt?: string }>
  }>
}

export interface ListSupportCannedRepliesResult {
  ok: boolean
  cannedReplies: Array<{
    id: string
    name: string
    language?: string
    body: string
    variables?: string[]
    tagAutoApply?: string[]
    createdAt?: string
    updatedAt?: string
  }>
}

/**
 * Fetches internal notes and summary for a support ticket.
 */
export async function getSupportTicketInternalNotes(
  ticketId: string,
  accessReason?: unknown,
): Promise<SupportTicketInternalNotesResult> {
  const fn = httpsCallable<{ ticketId: string; accessReason?: unknown }, SupportTicketInternalNotesResult>(
    firebaseFunctions,
    'getSupportTicketInternalNotes',
  )
  const res = await fn({ ticketId, ...(accessReason ? { accessReason } : {}) })
  return res.data
}

/**
 * Lists available canned replies, seeding defaults if the collection is empty.
 */
export async function listSupportCannedReplies(): Promise<ListSupportCannedRepliesResult> {
  const fn = httpsCallable<void, ListSupportCannedRepliesResult>(firebaseFunctions, 'listSupportCannedReplies')
  const res = await fn()
  return res.data
}

/**
 * Creates or updates a canned reply.
 */
export async function upsertSupportCannedReply(params: {
  id?: string
  name: string
  body: string
  variables?: string[]
  tagAutoApply?: string[]
  language?: string
}): Promise<{ ok: boolean; id: string }> {
  const fn = httpsCallable<typeof params, { ok: boolean; id: string }>(firebaseFunctions, 'upsertSupportCannedReply')
  const res = await fn(params)
  return res.data
}

/**
 * Deletes a canned reply.
 */
export async function deleteSupportCannedReply(params: {
  id: string
}): Promise<{ ok: boolean; id: string }> {
  const fn = httpsCallable<typeof params, { ok: boolean; id: string }>(firebaseFunctions, 'deleteSupportCannedReply')
  const res = await fn(params)
  return res.data
}

/**
 * Merges a child support ticket into a parent support ticket.
 * Child SLA stops, thread is inlined into parent, and audit row is written.
 */
export async function mergeSupportTickets(params: {
  parentId: string
  childId: string
}): Promise<{ ok: boolean; parentId: string; childId: string }> {
  const fn = httpsCallable<typeof params, { ok: boolean; parentId: string; childId: string }>(
    firebaseFunctions,
    'mergeSupportTickets',
  )
  const res = await fn(params)
  return res.data
}

/**
 * Lists all support macros.
 */
export async function listSupportMacros(): Promise<{
  ok: boolean
  macros: SupportMacro[]
}> {
  const fn = httpsCallable<void, { ok: boolean; macros: SupportMacro[] }>(firebaseFunctions, 'listSupportMacros')
  const res = await fn()
  return res.data
}

/**
 * Creates or updates a support macro.
 */
export async function upsertSupportMacro(params: {
  id?: string
  name: string
  description?: string
  steps: SupportMacroStep[]
  defaultReply?: string
  defaultTags?: string[]
  requiredRole?: Array<'founder' | 'support'>
}): Promise<{ ok: boolean; id: string }> {
  const fn = httpsCallable<typeof params, { ok: boolean; id: string }>(firebaseFunctions, 'upsertSupportMacro')
  const res = await fn(params)
  return res.data
}

/**
 * Executes a support macro on a ticket.
 */
export async function runSupportMacro(params: {
  macroId: string
  ticketId: string
}): Promise<{
  ok: boolean
  macroId: string
  ticketId: string
  stepsRun: string[]
  actionIds: string[]
  replyPosted: boolean
}> {
  const fn = httpsCallable<
    typeof params,
    {
      ok: boolean
      macroId: string
      ticketId: string
      stepsRun: string[]
      actionIds: string[]
      replyPosted: boolean
    }
  >(firebaseFunctions, 'runSupportMacro')
  const res = await fn(params)
  return res.data
}

/**
 * Submits CSAT feedback for a ticket (reporter-facing callable).
 */
export async function submitSupportCsat(params: {
  ticketId: string
  rating: number
  comment?: string
}): Promise<{ ok: boolean; ticketId: string; rating: number }> {
  const fn = httpsCallable<typeof params, { ok: boolean; ticketId: string; rating: number }>(
    firebaseFunctions,
    'submitSupportCsat',
  )
  const res = await fn(params)
  return res.data
}

/**
 * Look up a support ticket by attached debug session id.
 */
export async function getSupportTicketByDebugSession(params: {
  debugSessionId: string
}): Promise<{
  ok: boolean
  ticket: {
    id: string
    title: string
    status: string
    reporterEmail: string
    createdAt: string
  } | null
}> {
  const fn = httpsCallable<
    typeof params,
    {
      ok: boolean
      ticket: {
        id: string
        title: string
        status: string
        reporterEmail: string
        createdAt: string
      } | null
    }
  >(firebaseFunctions, 'getSupportTicketByDebugSession')
  const res = await fn(params)
  return res.data
}

/**
 * Title similarity check using Jaccard word-overlap (zero runtime dependencies).
 */
export function isTitleSimilar(a: string, b: string): boolean {
  if (!a || !b) return false
  const wordsA = new Set(a.toLowerCase().replace(/[^a-z0-9\s]/g, '').split(/\s+/).filter((w) => w.length > 2))
  const wordsB = new Set(b.toLowerCase().replace(/[^a-z0-9\s]/g, '').split(/\s+/).filter((w) => w.length > 2))
  if (wordsA.size === 0 || wordsB.size === 0) return false
  let intersection = 0
  for (const w of wordsA) {
    if (wordsB.has(w)) intersection++
  }
  const union = new Set([...wordsA, ...wordsB]).size
  return union > 0 && (intersection / union) >= 0.4
}

/**
 * Loads up to 10 prior tickets for the same reporter via server callable.
 */
export async function getPriorTicketsByReporter(
  reporterUid: string,
  excludeTicketId?: string,
  accessReason?: { code: string; linkedId?: string },
): Promise<SupportTicket[]> {
  if (!reporterUid) return []
  return callable<{ reporterUid: string; excludeTicketId?: string; accessReason?: unknown }, SupportTicket[]>(
    'listOwnerPriorTicketsByReporter',
    {
      reporterUid,
      excludeTicketId,
      accessReason: accessReason ?? (excludeTicketId ? { code: 'SUPPORT_TICKET', linkedId: excludeTicketId } : undefined),
    },
  )
}

/**
 * Loads active restrictions for a reporter.
 */
export async function getReporterRestriction(
  reporterUid: string,
): Promise<SupportReporterRestriction | null> {
  if (!reporterUid) return null
  try {
    const snap = await getDoc(doc(firestoreDb, 'support_reporter_restrictions', reporterUid))
    if (!snap.exists()) return null
    return snap.data() as SupportReporterRestriction
  } catch (err) {
    console.warn('Failed to load reporter restriction:', err)
    return null
  }
}

/**
 * Loads recent owner actions on this user from owner_action_log.
 */
export async function getRecentOwnerActionsForUser(
  userId: string,
): Promise<Array<{ id: string; kind: string; reason: string; createdAt: string; actorRole?: string }>> {
  if (!userId) return []
  try {
    const colRef = collection(firestoreDb, 'owner_action_log')
    const q = query(
      colRef,
      where('targetLabel', '==', userId),
      fsLimit(10),
    )
    const snap = await getDocs(q)
    return snap.docs.map((d) => {
      const data = d.data()
      return {
        id: d.id,
        kind: data.kind || '',
        reason: data.reason || '',
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate().toISOString() : data.createdAt || '',
        actorRole: data.actorRole || '',
      }
    })
  } catch (err) {
    console.warn('Failed to load recent owner actions:', err)
    return []
  }
}

/**
 * Loads reporter basic account info (e.g. tenure, email) via server callable.
 */
export async function getReporterAccountInfo(
  userId: string,
  accessReason?: { code?: string; ticketId?: string; linkedId?: string } | unknown,
): Promise<{ email?: string; tenure?: string; createdAt?: string } | null> {
  if (!userId) return null
  const reasonObj = accessReason && typeof accessReason === 'object' ? (accessReason as { code?: string; ticketId?: string; linkedId?: string }) : null
  const parsed = reasonObj
    ? {
        code: reasonObj.code || 'SUPPORT_TICKET',
        ticketId: reasonObj.ticketId || reasonObj.linkedId || 'TKT-REPORTER',
        ...reasonObj,
      }
    : accessReason
  return callable<{ userId: string; accessReason?: unknown }, { email?: string; tenure?: string; createdAt?: string } | null>(
    'getOwnerReporterAccount',
    {
      userId,
      accessReason: parsed,
    },
  )
}
